<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Broadcast',
  },
})

const { can } = useAbility()

const page = ref(1)
const perPage = ref(20)
const loading = ref(false)
const campaigns = ref([])
const totalItems = ref(0)

const headers = [
  { title: 'Template', key: 'template_name', sortable: false },
  { title: 'Jumlah Penerima', key: 'recipients_count', sortable: false },
  { title: 'Progress Dibuka', key: 'progress', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Dibuat', key: 'created_at', sortable: false },
  { title: '', key: 'actions', sortable: false, align: 'end', width: 60 },
]

const statusMeta = status => ({
  ready: { label: 'Siap Dikirim', color: 'info' },
  completed: { label: 'Selesai', color: 'success' },
  draft: { label: 'Draft', color: 'default' },
}[status] ?? { label: status, color: 'default' })

const fetchCampaigns = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/broadcast-campaigns', {
      query: { page: page.value, per_page: perPage.value },
    }).get()

    const body = response?.data ?? response

    campaigns.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    console.error('[BroadcastCampaigns] fetch error', err)
    campaigns.value = []
  } finally {
    loading.value = false
  }
}

watch(page, fetchCampaigns, { immediate: true })
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="pt-5 px-5 d-flex align-center justify-space-between">
          <span>Kirim Broadcast WhatsApp</span>
          <VBtn
            v-if="can('create', 'Broadcast')"
            :to="{ name: 'crm-broadcast-campaigns-create' }"
            prepend-icon="ri-add-line"
          >
            Buat Campaign
          </VBtn>
        </VCardTitle>
        <VCardText class="text-body-2 text-medium-emphasis px-5 pt-0">
          Tiap campaign adalah antrian pesan per customer — mengirim tetap klik manual satu-satu
          per penerima (bukan kirim otomatis sekali klik).
        </VCardText>

        <VDivider />

        <VDataTableServer
          v-model:page="page"
          :headers="headers"
          :items="campaigns"
          :items-length="totalItems"
          :items-per-page="perPage"
          :loading="loading"
          no-data-text="Belum ada campaign broadcast."
        >
          <template #item.template_name="{ item }">
            {{ item.template_name ?? '-' }}
          </template>

          <template #item.progress="{ item }">
            <div style="min-width: 140px">
              <VProgressLinear
                :model-value="item.recipients_count ? (item.opened_count / item.recipients_count) * 100 : 0"
                height="8"
                rounded
                color="success"
              />
              <span class="text-caption">{{ item.opened_count }} dari {{ item.recipients_count }} sudah dibuka</span>
            </div>
          </template>

          <template #item.status="{ item }">
            <VChip
              :color="statusMeta(item.status).color"
              size="small"
            >
              {{ statusMeta(item.status).label }}
            </VChip>
          </template>

          <template #item.created_at="{ item }">
            {{ item.created_at ? new Date(item.created_at).toLocaleString('id-ID') : '-' }}
          </template>

          <template #item.actions="{ item }">
            <IconBtn :to="{ name: 'crm-broadcast-campaigns-id', params: { id: item.id } }">
              <VIcon icon="ri-arrow-right-line" />
              <VTooltip activator="parent">
                Buka Antrian
              </VTooltip>
            </IconBtn>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>
</template>
